import { HttpClient, HttpContext, HttpErrorResponse } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { ApiTypes, Logger } from '@mode/shared/contract-common';
import { INITIATOR_CONTEXT, serializeAndStripIncludes, UriTemplateService } from '@mode/shared/util-halogen';
import * as angular from 'angular';
import { map, timeout } from 'rxjs/operators';
import { UrlExpanderService } from '../url-expander/url-expander.service';
import { ApiError } from './api.service';

// Don't know if this is the best name
export interface LinkOptions {
  method: string;
  url: string;
  timeout?: number;
  params?: any;
  transformResponse?: any;
}

export interface LinkParameters {
  includes?: ApiTypes.Includes;
}

export class ModeLink {
  templated = false;
  resourceType;
  href: any;
  parameters: any;

  constructor(
    resourceType,
    representation,
    private $http: HttpClient,
    private urlExpander: UrlExpanderService,
    private $log: Logger,
    private uriTemplateService: UriTemplateService
  ) {
    this.$initialize(resourceType, representation);
  }

  /**
   * Constructor logic
   * (which is called by the autogenerated constructor via ModeLink.extend)
   * @param {Object} [representation]  Initialize the resource with this representation.
   * @param {Object} [options]
   */
  $initialize(resourceType, representation) {
    this.resourceType = resourceType;

    if (representation) {
      angular.forEach(representation, (value, prop) => {
        this[prop] = value;
      });
    }
  }

  $follow(params: LinkParameters, timeoutMs, options) {
    const deferred = new Promise((resolve, reject) => {
      const passedParams = angular.copy(params);
      const includes = serializeAndStripIncludes(passedParams);

      this.$log.debug('Following', this.$expand(passedParams));

      const opts: LinkOptions = {
        method: options?.method || 'GET',
        url: this.$expand(passedParams),
        timeout: timeoutMs,
      };

      const request = this.$http
        .request(opts.method, opts.url, {
          observe: 'response',
          body: options?.data,
          params: includes,
          context: new HttpContext().set(INITIATOR_CONTEXT, new Error()),
        })
        .pipe(timeout(timeoutMs ?? 100000))
        .toPromise();

      const klass = this.resourceType;
      request.then(
        (response) => {
          // eslint-disable-next-line @typescript-eslint/no-explicit-any
          resolve(new klass((response as any).body || {}));
        },
        (failedResponse) => {
          if (failedResponse instanceof HttpErrorResponse) {
            // Not going to bubble up abort errors since we don't have any reason to handle them
            if (failedResponse.status !== -1) {
              reject(new ApiError(failedResponse));
            }
          } else {
            reject(failedResponse);
          }
        }
      );
    });

    return deferred;
  }

  $get(params, timeoutMs, options) {
    // params currently isn't used anywhere
    this.$log.debug('Getting', this.href);
    const longIntFlag = false;
    const deferred = new Promise((resolve, reject) => {
      const request = this.$http
        .get(this.href, {
          observe: 'response',
          responseType: options?.responseType || 'json',
          context: new HttpContext().set(INITIATOR_CONTEXT, new Error()),
        })
        .pipe(
          timeout(timeoutMs ?? 100000),
          map((response) => {
            return {
              config: {
                // To retain compatibility with result.js initialize function
                // eslint-disable-next-line @typescript-eslint/no-explicit-any
                longInt: (response as any).headers.has('x-has-long-number'),
              },
              // eslint-disable-next-line @typescript-eslint/no-explicit-any
              data: (response as any).body,
            };
          })
        )
        .toPromise();

      request
        .then((response) => {
          if (longIntFlag) {
            // patching angular return object so it can referenced in result.js
            (response.config as any).longInt = true;
          }
          resolve(response);
        })
        .catch((failedResponse) => {
          if (failedResponse instanceof HttpErrorResponse) {
            // Not going to bubble up abort errors since we don't have any reason to handle them
            if (failedResponse.status !== -1) {
              reject(new ApiError(failedResponse));
            }
          } else {
            reject(failedResponse);
          }
        });
    });

    return deferred;
  }

  /**
   * Generate the url for the $save, $fetch and $destroy methods.
   */
  $expand(params, opts?) {
    const expandedUrl = this.urlExpander.expand(this.href, opts);
    if (!this.templated) {
      return expandedUrl;
    }

    const defaults = {};
    if (this.parameters) {
      angular.forEach(this.parameters, (value, param) => {
        if (angular.isDefined(value['default'])) {
          defaults[param] = value['default'];
        }
      });
    }

    const combined = angular.extend(defaults, params);
    return this.uriTemplateService.parse(expandedUrl).expand(combined);
  }
}

@Injectable({ providedIn: 'root' })
export class ModeLinkFactoryService {
  constructor(
    private $http: HttpClient,
    private urlExpander: UrlExpanderService,
    private $log: Logger,
    private uriTemplateService: UriTemplateService
  ) {}

  create(resourceType, representation) {
    return new ModeLink(resourceType, representation, this.$http, this.urlExpander, this.$log, this.uriTemplateService);
  }
}

/**
 * Adapter to help migrate to Angular 2 dependency injector.
 * Migration Guide: If you're migrating a service that depends on this, update to ModeLinkFactoryService.create first.
 *
 * @deprecated Use ModeLinkFactoryService instead
 */
export function ModeLinkFactoryMigrationAdapter(modeLinkFactory: ModeLinkFactoryService) {
  return function (resourceType, representation) {
    modeLinkFactory.create(resourceType, representation);
  };
}

ModeLinkFactoryMigrationAdapter.$inject = ['ModeLinkFactory'];
